import React, { useState, useEffect } from "react";
import { Grid,  Button, Modal, Box, Typography } from "@mui/material";
import Portfolio from "./Portfolio";
import "../../Styles/strategy.css";
import { postRebalance, GetStrategyVariance} from "../../Redux/Actions/transactionService";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSelectedUserId } from "../../SelectedUserIdContext";
import customFailAlert from "../../Alerts/customeFailedAlert";
import Checkbox from '@mui/material/Checkbox';
import RebalanceConfirmationModal from './RebalanceConfirmationModal';
import RebalanceResultModal from './RebalanceResultModal';
import ManualRebalancePage from '../../Pages/Rebalancing/ManualRebalancePage';
import AutoRebalancePage from '../../Pages/Rebalancing/AutoRebalancePage';

// Add this style object near the top of the file with other constants
const userWiseModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '57%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1400px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
};

function Index() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const { selectedUserIds, setSelectedUserIds } = useSelectedUserId();
  const {  strategyData } = useSelector((state) => state.transaction);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [rebalanceData, setRebalanceData] = useState([]);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false); 
  const userInfoString = localStorage.getItem("userInfo");
  const userInfo = userInfoString && userInfoString !== "undefined" ? JSON.parse(userInfoString) : null;
  const role = userInfo?.portal || 'MANAGER';
  const custodian= strategyData[0]?.strategy?.custodian
  const [userWiseConfirmation, setUserWiseConfirmation] = useState(false);
  const [userWiseData, setUserWiseData] = useState([]);
  const [selectedWashTrades, setSelectedWashTrades] = useState({});
  const [canRebalance, setCanRebalance] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { userNames } = useSelector((state) => state.userStatus);
  const [showManual, setShowManual] = useState(false);
  const [selectedInvestorsForManual, setSelectedInvestorsForManual] = useState([]);
  const [showAuto, setShowAuto] = useState(false);
  const [selectedInvestorsForAuto, setSelectedInvestorsForAuto] = useState([]);
  
  // Function to handle manual rebalance with selected investors
  const handleSetShowManual = (show, selectedInvestors = []) => {
    setShowManual(show);
    if (show && selectedInvestors.length > 0) {
      setSelectedInvestorsForManual(selectedInvestors);
    } else {
      setSelectedInvestorsForManual([]);
    }
  };

  // Function to handle auto rebalance with selected investors
  const handleSetShowAuto = (show, selectedInvestors = []) => {
    setShowAuto(show);
    if (show && selectedInvestors.length > 0) {
      setSelectedInvestorsForAuto(selectedInvestors);
    } else {
      setSelectedInvestorsForAuto([]);
    }
  };
  
  useEffect(() => {
    setIsButtonDisabled(selectedUserIds.length === 0);
  }, [selectedUserIds]);

  useEffect(() => {
    setSelectedUserIds([]);
  }, []);

  useEffect(() => {
    const hasEnoughAssets = strategyData?.some(data => 
      data.assetsVariance && data.assetsVariance.length >= 2
    );
    setCanRebalance(hasEnoughAssets);
  }, [strategyData]);

  const EntityId= localStorage.getItem("entId")

  const handleConfirmRebalance = async () => {
    setConfirmationOpen(false);
    // Filter data for selected users only
    const userWiseAssets = strategyData
      .filter(userData => selectedUserIds.includes(userData.userRole.id))
      .map(userData => ({
        userId: userData.userRole.id,
        userEmail: userData.userRole.user?.email || '-',
        assets: userData.assetsVariance.map(asset => ({
          ...asset,
          symbol: asset.asset.symbol,
          currentAmount: parseFloat(asset.currentPortfolio || 0).toFixed(2),
          newAmount: parseFloat(asset.idealPortfolio || 0).toFixed(2),
          buySellAmount: (parseFloat(asset.idealPortfolio || 0) - parseFloat(asset.currentPortfolio || 0)).toFixed(2),
          isWashTrade: asset.isWashTrade,
          assetId: asset.asset.id
        }))
      }));

    setUserWiseData(userWiseAssets);
    setUserWiseConfirmation(true);
  };

  const handleFinalConfirm = async () => {
    // Create the body structure for the API
    const userRoleAssets = userWiseData.map(userData => ({
      userRoleId: userData.userId,
      assets: userData.assets.map(asset => ({
        symbol: asset.symbol,
        isWash: asset.isWashTrade && !selectedWashTrades[`${userData.userId}-${asset.assetId}`]
      }))
    }));

    const strategyBreakups = userWiseData.flatMap(userData =>
      userData.assets.map(asset => ({
        symbol: asset.symbol,
        amount: asset.amount,
        isWash: asset.isWashTrade && !selectedWashTrades[`${userData.userId}-${asset.assetId}`]
      }))
    );

    const body = {
      type:  "MKT" ,
      strategyId: id,
      userRoleAssets,
      orderItems: strategyBreakups
    };

    // Dispatch the action with the formatted body and handle the response
    const response = await dispatch(postRebalance({ body, role }));

    if (response.payload.statusCode === 201) {
      // Success: Close modal and show success message
      setUserWiseConfirmation(false);
      setResultOpen(true);
      setSelectedUserIds([]);
      setSelectedWashTrades({});
      if (id) {
        dispatch(GetStrategyVariance({ strategyId: id }));
      }
    } else {
      // Failure: Show failed alert
      customFailAlert("Rebalance failed. Please try again.");
    }
  };

  const handleWashTradeChange = (userId, assetId) => {
    setSelectedWashTrades(prev => ({
      ...prev,
      [`${userId}-${assetId}`]: !prev[`${userId}-${assetId}`]
    }));
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setShowModal(false);
  };

  // Get strategy name and format current date
  const strategyName = strategyData?.[0]?.strategy?.name || '-';

  return (
    <>
      {showManual ? (
        <ManualRebalancePage setShowManual={handleSetShowManual} selectedInvestors={selectedInvestorsForManual} />
      ) : showAuto ? (
        <AutoRebalancePage setShowAuto={handleSetShowAuto} selectedInvestors={selectedInvestorsForAuto} />
      ) : (
        <>
          <Grid item xs={12}>
            <Box 
              sx={{ 
                p: 3,
                backgroundColor: '#F8F9FA',
                borderBottom: '1px solid #E0E0E0',
                mb: 3
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  fontSize: "24px", 
                  color: "#1A1A1A",
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                Rebalancing 
                <Typography 
                  component="span" 
                  sx={{ 
                    ml: 1,
                    color: '#666666',
                    fontSize: "24px",
                    fontWeight: 400
                  }}
                >
                  | {strategyName}
                </Typography>
              </Typography>
            </Box>
          </Grid>

          <RebalanceConfirmationModal 
            open={confirmationOpen}
            onClose={() => setConfirmationOpen(false)}
            isFullscreen={isFullscreen}
            rebalanceData={rebalanceData}
            onConfirm={handleConfirmRebalance}
            setIsFullscreen={setIsFullscreen}
          />

          <Modal open={userWiseConfirmation} onClose={() => setUserWiseConfirmation(false)}>
            <Box sx={userWiseModalStyle}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  fontSize: '20px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.87)'
                }}
              >
                Confirm User- Wise Rebalance
              </Typography>

              <Box sx={{ 
                display: 'flex',
                gap: 3,
              }}>
                {/* Left side - Main rebalance table */}
                <Box sx={{ 
                  flex: 1,
                  maxHeight: 'calc(80vh - 250px)',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                    display: 'none'
                  },
                  '&:hover::-webkit-scrollbar': {
                    display: 'block'
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#555',
                    },
                  }
                }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                    backgroundColor: '#fff'
                  }}>
                    <colgroup>
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '25%' }} />
                    </colgroup>
                    <thead>
                      <tr style={{ backgroundColor: '#F8F9FA' }}>
                        <th style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left', 
                          fontWeight: 400,
                          fontSize: '14px',
                          color: 'rgba(0, 0, 0, 0.87)',
                          borderBottom: '1px solid #E0E0E0'
                        }}>
                          Investor
                        </th>
                        <th style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left', 
                          fontWeight: 400,
                          fontSize: '14px',
                          color: 'rgba(0, 0, 0, 0.87)',
                          borderBottom: '1px solid #E0E0E0'
                        }}>
                          Asset
                        </th>
                        <th style={{ 
                          padding: '12px 16px', 
                          textAlign: 'right', 
                          fontWeight: 400,
                          fontSize: '14px',
                          color: 'rgba(0, 0, 0, 0.87)',
                          borderBottom: '1px solid #E0E0E0'
                        }}>
                          Current ($)
                        </th>
                        <th style={{ 
                          padding: '12px 16px', 
                          textAlign: 'right', 
                          fontWeight: 400,
                          fontSize: '14px',
                          color: 'rgba(0, 0, 0, 0.87)',
                          borderBottom: '1px solid #E0E0E0'
                        }}>
                          New ($)
                        </th>
                        <th style={{ 
                          padding: '12px 16px', 
                          textAlign: 'right', 
                          fontWeight: 400,
                          fontSize: '14px',
                          color: 'rgba(0, 0, 0, 0.87)',
                          borderBottom: '1px solid #E0E0E0'
                        }}>
                          Buy/Sell ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {userWiseData.map((userData) =>
                        userData.assets.map((asset, assetIndex) => {
                          const buySellAmount = parseFloat(asset.buySellAmount);
                          const isFirstRow = assetIndex === 0;
                          // Find user data from strategyData
                          const userDataFromStrategy = strategyData.find(data => data.userRole.id === userData.userId);
                          const firstName = userDataFromStrategy?.userRole?.user?.userInfo?.firstName || '';
                          const lastName = userDataFromStrategy?.userRole?.user?.userInfo?.lastName || '';
                          const fullName = `${firstName} ${lastName}`.trim() || '-';
                          
                          return (
                            <tr key={`${userData.userId}-${assetIndex}`}>
                              {isFirstRow && (
                                <td 
                                  rowSpan={userData.assets.length}
                                  style={{ 
                                    padding: '12px 16px',
                                    borderBottom: '1px solid #E0E0E0',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {fullName}
                                </td>
                              )}
                              <td style={{ 
                                padding: '12px 16px', 
                                borderBottom: '1px solid #E0E0E0',
                                whiteSpace: 'nowrap'
                              }}>
                                {asset.symbol}
                              </td>
                              <td style={{ 
                                padding: '12px 16px', 
                                textAlign: 'right', 
                                borderBottom: '1px solid #E0E0E0'
                              }}>
                                {parseFloat(asset.currentAmount).toFixed(2)}
                              </td>
                              <td style={{ 
                                padding: '12px 16px', 
                                textAlign: 'right', 
                                borderBottom: '1px solid #E0E0E0'
                              }}>
                                {parseFloat(asset.newAmount).toFixed(2)}
                              </td>
                              <td style={{ 
                                padding: '12px 16px', 
                                textAlign: 'right',
                                color: buySellAmount < 0 ? '#D32F2F' : '#2E7D32',
                                borderBottom: '1px solid #E0E0E0'
                              }}>
                                {buySellAmount < 0 ? `(${Math.abs(buySellAmount).toFixed(2)})` : buySellAmount.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </Box>

                {/* Right side - Wash Trade Warning - Entire section in a container */}
                <Box sx={{ 
                  width: '500px',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 3,
                  maxHeight: 'calc(80vh - 250px)',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2,
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'rgba(0, 0, 0, 0.87)'
                    }}
                  >
                    Warning: Risk of Wash Sale Trades
                  </Typography>

                  {/* Pink warning box */}
                  <Box sx={{ 
                    backgroundColor: '#FFF3F3',
                    p: 2,
                    mb: 3,
                    borderRadius: '4px'
                  }}>
                    <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.87)' }}>
                      The following trades under your order meet conditions for "Wash Sale" trade, and may get flagged by the SEC.
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '14px', 
                      color: 'rgba(0, 0, 0, 0.87)', 
                      mt: 1,
                      fontWeight: 700
                    }}>
                      Select the trades you'd like to proceed with anyway.
                    </Typography>
                  </Box>

                  {/* Table */}
                  <Box sx={{ 
                    overflow: 'auto',
                    flex: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      height: '8px',
                      display: 'none'
                    },
                    '&:hover::-webkit-scrollbar': {
                      display: 'block'
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#888',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#555',
                      },
                    }
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ 
                            padding: '8px',
                            textAlign: 'left',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: 'rgba(0, 0, 0, 0.87)',
                            width: '20%'
                          }}>Investor</th>
                          <th style={{ width: '5%' }}></th> {/* Checkbox column */}
                          <th style={{ 
                            padding: '8px',
                            textAlign: 'left',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: 'rgba(0, 0, 0, 0.87)',
                            width: '25%'
                          }}>Asset</th>
                          <th style={{ 
                            padding: '8px',
                            textAlign: 'right',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: 'rgba(0, 0, 0, 0.87)',
                            width: '15%'
                          }}>Price</th>
                          <th style={{ 
                            padding: '8px',
                            textAlign: 'right',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: 'rgba(0, 0, 0, 0.87)',
                            width: '15%'
                          }}>Quantity</th>
                          <th style={{ 
                            padding: '8px',
                            textAlign: 'right',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: 'rgba(0, 0, 0, 0.87)',
                            width: '20%'
                          }}>Value($)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userWiseData
                          .filter(userData => userData.assets.some(asset => asset.isWashTrade))
                          .map((userData) => {
                            const userDataFromStrategy = strategyData.find(data => data.userRole.id === userData.userId);
                            const firstName = userDataFromStrategy?.userRole?.user?.userInfo?.firstName || '';
                            const lastName = userDataFromStrategy?.userRole?.user?.userInfo?.lastName || '';
                            const fullName = `${firstName} ${lastName}`.trim() || '-';
                            const washTradeAssets = userData.assets.filter(asset => asset.isWashTrade);

                            return washTradeAssets.map((asset, assetIndex) => (
                              <tr key={`${userData.userId}-${assetIndex}`}>
                                {assetIndex === 0 && (
                                  <td 
                                    rowSpan={washTradeAssets.length}
                                    style={{ 
                                      padding: '8px',
                                      fontSize: '14px',
                                      color: 'rgba(0, 0, 0, 0.87)',
                                      verticalAlign: 'middle'
                                    }}
                                  >
                                    {fullName}
                                  </td>
                                )}
                                <td style={{ padding: '8px' }}>
                                  <Checkbox
                                    checked={!!selectedWashTrades[`${userData.userId}-${asset.assetId}`]}
                                    onChange={() => handleWashTradeChange(userData.userId, asset.assetId)}
                                    size="small"
                                  />
                                </td>
                                <td style={{ 
                                  padding: '8px',
                                  fontSize: '14px',
                                  color: 'rgba(0, 0, 0, 0.87)'
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* <img 
                                      src={`/asset-icons/${asset.symbol.toLowerCase()}.svg`} 
                                      alt={asset.symbol}
                                      style={{ width: 20, height: 20 }}
                                    /> */}
                                    {asset.symbol}
                                  </Box>
                                </td>
                                <td style={{ 
                                  padding: '8px',
                                  textAlign: 'right',
                                  fontSize: '14px',
                                  color: 'rgba(0, 0, 0, 0.87)'
                                }}>
                                  ${parseFloat(asset.price).toFixed(2)}
                                </td>
                                <td style={{ 
                                  padding: '8px',
                                  textAlign: 'right',
                                  fontSize: '14px',
                                  color: 'rgba(0, 0, 0, 0.87)'
                                }}>
                                  {parseFloat(asset.amount).toFixed(2)}
                                </td>
                                <td style={{ 
                                  padding: '8px',
                                  textAlign: 'right',
                                  fontSize: '14px',
                                  color: 'rgba(0, 0, 0, 0.87)'
                                }}>
                                  ${(parseFloat(asset.price) * parseFloat(asset.amount)).toFixed(2)}
                                </td>
                              </tr>
                            ));
                          })}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ 
                mt: 3,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2
              }}>
                <Button
                  onClick={() => setUserWiseConfirmation(false)}
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    px: 3,
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleFinalConfirm}
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    px: 3,
                    fontSize: '14px',
                    backgroundColor: '#1976d2'
                  }}
                >
                  Confirm
                </Button>
              </Box>
            </Box>
          </Modal>

          <RebalanceResultModal 
            open={resultOpen}
            onClose={handleResultClose}
          />

          <Grid 
            item 
            xs={12} 
            sx={{ 
              height: "calc(100vh - 200px)", 
              overflowY: "auto", 
              overflowX: "hidden",
              '&::-webkit-scrollbar': {
                width: '6px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#BBC1CC',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#A3ABC6',
                },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: '#BBC1CC transparent',
            }}
          >
                            <Portfolio onSelectionChange={setSelectedUserIds} setShowManual={handleSetShowManual} setShowAuto={handleSetShowAuto} />
          </Grid>
        </>
      )}
    </>
  );
}

export default Index;

